import React, { useState, useMemo, useRef } from 'react';
import { shareReportAsPdf, downloadReportAsPdf } from '../../../utils/reportPdfShare';
import { CheckCircle2, XCircle, AlertTriangle, ShieldCheck, Copy, Check, Share2, Download } from 'lucide-react';

// ── SugarHathi brand palette (matches GRAD in do_finance_new.jsx) ─────────
const ACCENT = '#f97316';
const ACCENT_DARK = '#ea580c';
const BG = '#fffbf5';
const INK = '#1f2937';
const SLATE = '#6b7280';
const LINE = '#f0e6d8';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const GREEN = '#16a34a';
const AMBER = '#d97706';
const RED = '#dc2626';

// ── GSTIN Detailed sample response (Decentro), with filingDate added to
// demonstrate on-time vs late detection. Real payload carries filingDate
// on every row already — just wasn't used for delay calc before.
const SAMPLE = {
  status: 'SUCCESS',
  kycResult: {
    pan: 'ADKPC3279Q',
    gstin: '36ADKPC3279Q1ZY',
    legalName: 'CHERUKURI RAJENDRA',
    tradeName: 'BHARATIYA NATURAL PRODUCTS',
    gstnStatus: 'Active',
    taxpayerType: 'Regular',
    natureOfBusiness: ['Retail Business', 'Works Contract', 'Factory / Manufacturing', 'Office / Sale Office'],
    registrationDate: '16/10/2019',
    registrationType: 'NORMAL COMPOSITE CASUAL',
    stateJurisdiction: 'Moosapet',
    centralJurisdiction: 'KPHB',
    constitutionOfBusiness: 'Proprietorship',
    annualAggregateTurnover: '10Cr-20Cr',
    principalPlaceOfBusiness: '1st floor, H.No.3-6-69/B/13, Plot No. 92, Avanthi Nagar, Basheerbagh, Hyderabad, Hyderabad, Telangana, 500018',
    natureOfCoreBusinessActivity: 'Service Provider and Others',
    businessDetails: [
      { hsn: '2304', type: 'GOODS', description: 'OIL-CAKE AND OTHER SOLID RESIDUES FROM SOYABEAN OIL EXTRACTION' },
      { hsn: '3826', type: 'GOODS', description: 'BIODIESEL AND MIXTURES THEREOF' },
    ],
    filingStatus: [
      { filingYear: '2025-2026', filingPeriod: 'June',      filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '11/07/2025' },
      { filingYear: '2025-2026', filingPeriod: 'July',      filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '11/08/2025' },
      { filingYear: '2025-2026', filingPeriod: 'August',    filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '12/09/2025' },
      { filingYear: '2025-2026', filingPeriod: 'September', filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '12/10/2025' },
      { filingYear: '2025-2026', filingPeriod: 'October',   filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '27/11/2025' },
      { filingYear: '2025-2026', filingPeriod: 'November',  filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '29/12/2025' },
      { filingYear: '2025-2026', filingPeriod: 'December',  filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '11/01/2026' },
      { filingYear: '2025-2026', filingPeriod: 'January',   filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '11/02/2026' },
      { filingYear: '2025-2026', filingPeriod: 'February',  filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '11/03/2026' },
      { filingYear: '2025-2026', filingPeriod: 'March',     filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '12/04/2026' },
      { filingYear: '2026-2027', filingPeriod: 'April',     filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '11/05/2026' },
      { filingYear: '2026-2027', filingPeriod: 'May',       filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '11/06/2026' },
      { filingYear: '2026-2027', filingPeriod: 'June',      filingGstType: 'GSTR1',  filingStatus: 'Filed', filingDate: '11/07/2026' },
      { filingYear: '2026-2027', filingPeriod: 'July',      filingGstType: 'GSTR1',  filingStatus: 'Not Filed', filingDate: null },
      { filingYear: '2025-2026', filingPeriod: 'June',      filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '20/07/2025' },
      { filingYear: '2025-2026', filingPeriod: 'July',      filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '21/08/2025' },
      { filingYear: '2025-2026', filingPeriod: 'August',    filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '20/09/2025' },
      { filingYear: '2025-2026', filingPeriod: 'September', filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '25/10/2025' },
      { filingYear: '2025-2026', filingPeriod: 'October',   filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '27/11/2025' },
      { filingYear: '2025-2026', filingPeriod: 'November',  filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '29/12/2025' },
      { filingYear: '2025-2026', filingPeriod: 'December',  filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '20/01/2026' },
      { filingYear: '2025-2026', filingPeriod: 'January',   filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '23/02/2026' },
      { filingYear: '2025-2026', filingPeriod: 'February',  filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '31/03/2026' },
      { filingYear: '2025-2026', filingPeriod: 'March',     filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '20/04/2026' },
      { filingYear: '2026-2027', filingPeriod: 'April',     filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '21/05/2026' },
      { filingYear: '2026-2027', filingPeriod: 'May',       filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '20/06/2026' },
      { filingYear: '2026-2027', filingPeriod: 'June',      filingGstType: 'GSTR3B', filingStatus: 'Filed', filingDate: '20/07/2026' },
      { filingYear: '2024-2025', filingPeriod: 'Annual', filingGstType: 'GSTR9', filingStatus: 'Not Filed', filingDate: null },
      { filingYear: '2023-2024', filingPeriod: 'Annual', filingGstType: 'GSTR9', filingStatus: 'Filed', filingDate: '19/05/2026' },
      { filingYear: '2023-2024', filingPeriod: 'Annual', filingGstType: 'GSTR9C', filingStatus: 'Filed', filingDate: '20/05/2026' },
    ],
  },
  decentroTxnId: '2DAEC6D4A66A4692A20530F96B9005DD',
  responseCode: 'S00000',
  requestTimestamp: '2026-08-07 16:24:36.709602 IST (GMT +0530)',
  responseTimestamp: '2026-08-07 16:24:38.636073 IST (GMT +0530)',
};

const MONTH_ORDER = ['April','May','June','July','August','September','October','November','December','January','February','March'];
const MONTH_INDEX = { April:3, May:4, June:5, July:6, August:7, September:8, October:9, November:10, December:11, January:0, February:1, March:2 };
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// "16/10/2019" -> "16 Oct 2019"
function formatDate(ddmmyyyy) {
  if (!ddmmyyyy) return null;
  const [d, m, y] = ddmmyyyy.split('/');
  const mi = parseInt(m, 10) - 1;
  if (mi < 0 || mi > 11) return ddmmyyyy;
  return `${parseInt(d, 10)} ${MONTH_SHORT[mi]} ${y}`;
}

// Statutory due-date rules for MONTHLY filers (GSTR1: 11th, GSTR3B: 20th of
// the following month). QRMP/quarterly filers have different due dates —
// not distinguishable from this API response, so this assumes monthly
// filing. Government due-date extensions during the period aren't
// reflected here either — treat "late" as indicative, not authoritative.
function dueDateFor(filingYear, filingPeriod, gstType) {
  const monthIdx = MONTH_INDEX[filingPeriod];
  if (monthIdx === undefined) return null; // Annual returns have no fixed monthly due date here
  const [fyStart] = filingYear.split('-').map(Number);
  // April=3 -> calendar year fyStart, Jan/Feb/Mar (0,1,2) -> fyStart+1
  const calYear = monthIdx >= 3 ? fyStart : fyStart + 1;
  const dueDay = gstType === 'GSTR3B' ? 20 : 11;
  // due date is in the month AFTER the filing period
  const dueMonth = monthIdx + 1; // JS month index, may roll to 12 -> handled by Date()
  return new Date(calYear, dueMonth, dueDay);
}

function parseDDMMYYYY(s) {
  if (!s) return null;
  const [d, m, y] = s.split('/').map(Number);
  return new Date(y, m - 1, d);
}

function delayInfo(filing) {
  if (filing.filingStatus !== 'Filed' || !filing.filingDate) return { state: 'not_filed', days: null };
  const due = dueDateFor(filing.filingYear, filing.filingPeriod, filing.filingGstType);
  if (!due) return { state: 'filed', days: null }; // annual — no due-date rule applied
  const filed = parseDDMMYYYY(filing.filingDate);
  const diffDays = Math.round((filed - due) / (1000 * 60 * 60 * 24));
  if (diffDays > 0) return { state: 'late', days: diffDays };
  return { state: 'on_time', days: diffDays };
}

// ── GST Compliance Score ─────────────────────────────────────
// Derived, in-house score (not from any vendor) — weighs regularity,
// timeliness, recent trend, and annual-return compliance. Weights are a
// judgment call, meant to be calibrated against real applicant data over
// time, not a regulatory standard.
function computeComplianceScore(filingStatus) {
  const monthly = filingStatus.filter(f => f.filingGstType === 'GSTR1' || f.filingGstType === 'GSTR3B');
  const annual = filingStatus.filter(f => f.filingGstType === 'GSTR9' || f.filingGstType === 'GSTR9C');

  // 1. Regularity (40 pts) — filed / total, monthly returns
  const filedCount = monthly.filter(f => f.filingStatus === 'Filed').length;
  const totalCount = monthly.length;
  const regularityPct = totalCount ? filedCount / totalCount : 0;
  const regularityScore = regularityPct * 40;

  // 2. Timeliness (35 pts) — avg delay days across filed returns, linear decay
  const delays = monthly.map(delayInfo).filter(d => d.state === 'late' || d.state === 'on_time');
  const avgDelay = delays.length ? delays.reduce((s, d) => s + Math.max(0, d.days), 0) / delays.length : 0;
  const timelinessScore = Math.max(0, 35 * (1 - Math.min(avgDelay, 30) / 30));

  // 3. Trend (15 pts) — compare most recent quarter vs the one before it
  const chronological = [...monthly].sort((a, b) => {
    const da = dueDateFor(a.filingYear, a.filingPeriod, a.filingGstType);
    const db = dueDateFor(b.filingYear, b.filingPeriod, b.filingGstType);
    return (da || 0) - (db || 0);
  });
  const recentQ = chronological.slice(-6); // last ~3 months x 2 return types
  const priorQ = chronological.slice(-12, -6);
  const qScore = (arr) => {
    if (!arr.length) return null;
    const filed = arr.filter(f => f.filingStatus === 'Filed').length;
    const d = arr.map(delayInfo).filter(x => x.state === 'late' || x.state === 'on_time');
    const avgD = d.length ? d.reduce((s, x) => s + Math.max(0, x.days), 0) / d.length : 0;
    return (filed / arr.length) * 0.5 + (1 - Math.min(avgD, 30) / 30) * 0.5;
  };
  const recentScore = qScore(recentQ);
  const priorScore = qScore(priorQ);
  let trendScore = 7.5, trendLabel = 'Stable';
  if (recentScore !== null && priorScore !== null) {
    const delta = recentScore - priorScore;
    if (delta > 0.05) { trendScore = 15; trendLabel = 'Improving'; }
    else if (delta < -0.05) { trendScore = 0; trendLabel = 'Declining'; }
  }

  // 4. Annual return compliance (10 pts)
  let annualScore = 5, annualLabel = 'Not yet due';
  if (annual.length) {
    const anyOverdue = annual.some(f => f.filingStatus === 'Not Filed');
    const allFiled = annual.every(f => f.filingStatus === 'Filed');
    if (allFiled) { annualScore = 10; annualLabel = 'Filed'; }
    else if (anyOverdue) { annualScore = 0; annualLabel = 'Overdue'; }
  }

  const total = Math.round(regularityScore + timelinessScore + trendScore + annualScore);
  const band = total >= 85 ? 'Excellent' : total >= 70 ? 'Good' : total >= 50 ? 'Fair' : 'Poor';
  const bandTone = total >= 85 ? 'good' : total >= 70 ? 'good' : total >= 50 ? 'warn' : 'bad';

  return {
    total, band, bandTone,
    breakdown: [
      { label: 'Regularity', score: Math.round(regularityScore), max: 40, detail: `${filedCount}/${totalCount} filed` },
      { label: 'Timeliness', score: Math.round(timelinessScore), max: 35, detail: `${Math.round(avgDelay)}d avg delay` },
      { label: 'Recent Trend', score: trendScore, max: 15, detail: trendLabel },
      { label: 'Annual Returns', score: annualScore, max: 10, detail: annualLabel },
    ],
  };
}

function StatusPill({ label, tone }) {
  const map = {
    good: { bg: '#f0fdf4', color: GREEN, border: '#bbf7d0', Icon: CheckCircle2 },
    warn: { bg: '#fffbeb', color: AMBER, border: '#fde68a', Icon: AlertTriangle },
    bad:  { bg: '#fef2f2', color: RED, border: '#fecaca', Icon: XCircle },
  };
  const s = map[tone] || map.good;
  const Icon = s.Icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      <Icon size={12} strokeWidth={2.75} />
      {label}
    </span>
  );
}

function Row({ label, value, mono, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: '6px 0', borderBottom: `1px solid ${LINE}` }}>
      <span style={{ fontSize: 12, color: SLATE, fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: INK, fontWeight: 600, textAlign: 'right', fontFamily: mono ? MONO : 'inherit', wordBreak: 'break-word', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
        {value ?? '—'}
        {action}
      </span>
    </div>
  );
}

function SectionCard({ title, children, footer }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: '14px 16px', marginBottom: 8 }}>
      <h3 style={{ fontSize: 12, fontWeight: 800, color: ACCENT_DARK, margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </h3>
      <div>{children}</div>
      {footer && <div style={{ paddingTop: 10, marginTop: 2 }}>{footer}</div>}
    </div>
  );
}

// 3-state heatmap: green = on-time, amber = filed late, red = not filed
function FilingHeatmap({ filings, gstType }) {
  const rows = filings.filter(f => f.filingGstType === gstType);
  const byYear = {};
  rows.forEach(f => {
    if (!byYear[f.filingYear]) byYear[f.filingYear] = {};
    byYear[f.filingYear][f.filingPeriod] = f;
  });
  const years = Object.keys(byYear).sort();
  const filedCount = rows.filter(r => r.filingStatus === 'Filed').length;
  const lateCount = rows.filter(r => delayInfo(r).state === 'late').length;
  const totalCount = rows.length;

  if (totalCount === 0) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: INK }}>{gstType}</span>
        <span style={{ fontSize: 11, color: SLATE, fontWeight: 600 }}>
          {filedCount}/{totalCount} filed{lateCount > 0 ? ` · ${lateCount} late` : ''}
        </span>
      </div>
      {years.map(year => (
        <div key={year} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <span style={{ fontSize: 9.5, color: SLATE, width: 52, flexShrink: 0, fontFamily: MONO }}>{year}</span>
          <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {MONTH_ORDER.map(month => {
              const filing = byYear[year][month];
              if (!filing) return <div key={month} style={{ width: 14, height: 14, borderRadius: 3, background: '#f3f4f6' }} title={`${month}: no data`} />;
              const info = delayInfo(filing);
              const color = info.state === 'not_filed' ? RED : info.state === 'late' ? AMBER : GREEN;
              const tip = info.state === 'not_filed'
                ? `${month} ${year}: Not filed`
                : info.state === 'late'
                  ? `${month} ${year}: Filed ${info.days}d late (${filing.filingDate})`
                  : `${month} ${year}: On time (${filing.filingDate})`;
              return <div key={month} title={tip} style={{ width: 14, height: 14, borderRadius: 3, background: color }} />;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ScoreBadge({ result }) {
  const toneColor = { good: GREEN, warn: AMBER, bad: RED };
  const color = toneColor[result.bandTone];
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: '16px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
          border: `4px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 17, fontWeight: 800, color,
        }}>
          {result.total}
        </div>
        <div>
          <div style={{ fontSize: 10.5, color: SLATE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            GST Compliance Score
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color }}>{result.band}</div>
        </div>
      </div>
      <div>
        {result.breakdown.map((b, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
              <span style={{ color: SLATE, fontWeight: 600 }}>{b.label} · {b.detail}</span>
              <span style={{ color: INK, fontWeight: 700 }}>{b.score}/{b.max}</span>
            </div>
            <div style={{ height: 5, background: BG, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(b.score / b.max) * 100}%`, background: color, borderRadius: 3 }} />
            </div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 9.5, color: SLATE, marginTop: 4, lineHeight: 1.4 }}>
        In-house score derived from filing regularity, timeliness, recent trend, and annual-return compliance.
        Not a vendor/bureau score — weights are SugarHathi's own model, calibrated for DO Finance underwriting.
      </p>
    </div>
  );
}

export default function GstinAdminReport({ data = SAMPLE }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const k = data.kycResult;

  const stats = useMemo(() => {
    const byType = {};
    k.filingStatus.forEach(f => {
      if (!byType[f.filingGstType]) byType[f.filingGstType] = { filed: 0, total: 0 };
      byType[f.filingGstType].total++;
      if (f.filingStatus === 'Filed') byType[f.filingGstType].filed++;
    });
    const totalFiled = k.filingStatus.filter(f => f.filingStatus === 'Filed').length;
    const totalAll = k.filingStatus.length;
    const delays = k.filingStatus.map(delayInfo).filter(d => d.state === 'late');
    const avgDelay = delays.length ? Math.round(delays.reduce((s, d) => s + d.days, 0) / delays.length) : 0;
    return {
      byType, totalFiled, totalAll,
      regularity: totalAll ? Math.round((totalFiled / totalAll) * 100) : 0,
      lateCount: delays.length, avgDelay,
    };
  }, [k.filingStatus]);

  const complianceScore = useMemo(() => computeComplianceScore(k.filingStatus), [k.filingStatus]);

  const copyTxnId = () => {
    navigator.clipboard?.writeText(data.decentroTxnId).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadReportAsPdf(cardRef, 'gstin-detailed-admin-report.pdf', BG); } finally { setPdfBusy(false); }
  };
  const handleShare = async () => {
    const shareText = `GSTIN Detailed — ${k.tradeName}\nGSTIN: ${k.gstin}\nStatus: ${k.gstnStatus}\nCompliance Score: ${complianceScore.total}/100 (${complianceScore.band})`;
    setPdfBusy(true);
    try { await shareReportAsPdf(cardRef, 'gstin-detailed-admin-report.pdf', 'GSTIN Detailed — Admin Report', shareText, BG); } finally { setPdfBusy(false); }
  };

  const monthlyTypes = ['GSTR1', 'GSTR3B'];
  const annualTypes = k.filingStatus.filter(f => !monthlyTypes.includes(f.filingGstType));

  return (
    <div style={{ minHeight: '100%', background: BG, padding: '20px 14px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{`
        @media print { .no-print { display: none !important; } body { background: #fff !important; } }
      `}</style>
      <div ref={cardRef} style={{ maxWidth: 580, width: '100%', margin: '0 auto' }}>

        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button
            onClick={handleShare} disabled={pdfBusy}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px',
              color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Share2 size={13} />
            Share
          </button>
          <button
            onClick={handleDownloadPdf} disabled={pdfBusy}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px',
              color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Download size={13} />
            Download as PDF
          </button>
        </div>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`, borderRadius: 18, padding: '18px 20px', position: 'relative', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                <ShieldCheck size={15} color="#fff" strokeWidth={2.5} />
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  GSTIN Detailed · Credit Report
                </span>
              </div>
              <h1 style={{ color: '#fff', fontSize: 17, fontWeight: 800, margin: 0 }}>{k.tradeName}</h1>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11.5, margin: '2px 0 0', fontFamily: MONO, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>{k.gstin}</span>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>·</span>
                <span>Reg: {formatDate(k.registrationDate)}</span>
              </p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 9, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle2 size={11} color="#fff" strokeWidth={3} />
              <span style={{ color: '#fff', fontSize: 10.5, fontWeight: 800 }}>{k.gstnStatus}</span>
            </div>
          </div>
        </div>

        {/* SugarHathi Compliance Score */}
        <ScoreBadge result={complianceScore} />

        {/* Identity */}
        <SectionCard title="Business Identity">
          <Row label="Legal Name" value={k.legalName} />
          <Row label="Trade Name" value={k.tradeName} />
          <Row label="PAN" value={k.pan} mono />
          <Row label="Constitution" value={k.constitutionOfBusiness} />
          <Row label="Taxpayer Type" value={k.taxpayerType} />
          <Row label="Registration Type" value={k.registrationType} />
          <Row label="Annual Turnover" value={k.annualAggregateTurnover} />
          <Row label="Core Business Activity" value={k.natureOfCoreBusinessActivity} />
          <Row label="Nature of Business" value={k.natureOfBusiness.join(', ')} />
          <Row label="State Jurisdiction" value={k.stateJurisdiction} />
          <Row label="Central Jurisdiction" value={k.centralJurisdiction} />
          <Row label="Principal Place" value={k.principalPlaceOfBusiness} />
        </SectionCard>

        {/* HSN / Business details */}
        <SectionCard title="Goods / Services (HSN)">
          {k.businessDetails.map((b, i) => (
            <Row key={i} label={`${b.hsn} · ${b.type}`} value={b.description} />
          ))}
        </SectionCard>

        {/* Filing compliance summary */}
        <SectionCard
          title="Filing Compliance Summary"
          footer={
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill label={`${stats.totalFiled}/${stats.totalAll} filed`} tone={stats.regularity >= 90 ? 'good' : stats.regularity >= 70 ? 'warn' : 'bad'} />
              <StatusPill label={`${stats.regularity}% regularity`} tone={stats.regularity >= 90 ? 'good' : stats.regularity >= 70 ? 'warn' : 'bad'} />
              {stats.lateCount > 0
                ? <StatusPill label={`${stats.lateCount} late · avg ${stats.avgDelay}d delay`} tone={stats.avgDelay > 15 ? 'bad' : 'warn'} />
                : <StatusPill label="No late filings" tone="good" />
              }
            </div>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 8 }}>
            {Object.entries(stats.byType).map(([type, s]) => (
              <div key={type} style={{ background: BG, border: `1px solid ${LINE}`, borderRadius: 10, padding: '8px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 10.5, color: SLATE, fontWeight: 700 }}>{type}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: s.filed === s.total ? GREEN : RED }}>
                  {s.filed}/{s.total}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Filing history heatmap — GSTR1 / GSTR3B monthly, 3-state */}
        <SectionCard title="Monthly Filing History">
          {monthlyTypes.map(t => <FilingHeatmap key={t} filings={k.filingStatus} gstType={t} />)}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 10, color: SLATE, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: GREEN, display: 'inline-block' }} /> On time
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: AMBER, display: 'inline-block' }} /> Filed late
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: RED, display: 'inline-block' }} /> Not filed
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: '#f3f4f6', display: 'inline-block' }} /> No data
            </span>
          </div>
          <p style={{ fontSize: 9.5, color: SLATE, marginTop: 8, lineHeight: 1.4 }}>
            Due dates assume monthly filing (GSTR1: 11th, GSTR3B: 20th of following month). Quarterly (QRMP) filers and
            government due-date extensions aren't distinguishable from this data — treat "late" as indicative, not authoritative.
          </p>
        </SectionCard>

        {/* Annual returns */}
        {annualTypes.length > 0 && (
          <SectionCard title="Annual Returns (GSTR9 / GSTR9C)">
            {annualTypes.map((f, i) => (
              <Row key={i} label={`${f.filingGstType} · FY ${f.filingYear}`} value={f.filingStatus} />
            ))}
          </SectionCard>
        )}

        {/* Transaction metadata */}
        <SectionCard title="Transaction Metadata">
          <Row
            label="Vendor Txn ID"
            value={data.decentroTxnId}
            mono
            action={
              <button onClick={copyTxnId} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? GREEN : ACCENT_DARK, display: 'inline-flex' }}>
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            }
          />
          <Row label="Response Code" value={data.responseCode} mono />
          <Row label="Requested" value={data.requestTimestamp} mono />
          <Row label="Responded" value={data.responseTimestamp} mono />
        </SectionCard>

        <p style={{ fontSize: 10, color: SLATE, textAlign: 'center', marginTop: 6 }}>
          Filing history trimmed for artifact preview — production data holds full ~135-row history per applicant.
        </p>
      </div>
    </div>
  );
}
